/**
 * POS Settings — Staff & PIN Access management.
 *
 * Route:  /pos/settings   (feature-gated on `pos`, requiredRole="admin")
 *
 * Visual system — native RestroPulse admin platform. This screen sits
 * INSIDE the admin shell (Layout renders the top navbar around it), so
 * it uses only:
 *   • `--color-primary` (#143E30) for primary actions and active state
 *   • `#1A2421` for primary text, `#5C6B64` for muted
 *   • `#E2E8DF` / `#EEF1EE` for borders, white for surfaces
 *   • `#F9F9F8` for the page background (matches AdminOutlets pattern)
 * No module-specific palette, no dark-navy workspace.
 *
 * Sprint 1 correction · Authenticates with the platform JWT (not a POS
 * session token). A tenant admin can therefore open this screen and
 * create the FIRST POS staff PIN without a chicken-and-egg problem.
 *
 * PIN handling is explicit: a generated PIN is displayed exactly once
 * in a dialog. There is no endpoint, screen or export that can return
 * a PIN after generation.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Users, Plus, KeyRound, Power, ArrowLeft, ShieldAlert, Store } from 'lucide-react';
import PosAppearanceSection from './PosAppearanceSection';
import PosInvoiceConfigSection from './PosInvoiceConfigSection';
import PosSecuritySettingsSection from './PosSecuritySettingsSection';

// The full POS staff role list. Order mirrors the escalation ladder.
const ROLE_OPTIONS = [
  { value: 'steward',      label: 'Steward' },
  { value: 'bartender',    label: 'Bartender' },
  { value: 'cashier',      label: 'Cashier' },
  { value: 'supervisor',   label: 'Supervisor' },
  { value: 'manager',      label: 'Manager' },
  { value: 'outlet_admin', label: 'Outlet Admin' },
];

export default function PosSettings() {
  const navigate = useNavigate();
  const { user, API, getHeaders } = useAuth();

  // Outlet picker — admins are not outlet-scoped, so the settings page
  // needs an explicit selection. Managers with a single outlet get a
  // pre-filled read-only value.
  const [outlets, setOutlets] = useState([]);
  const [outletId, setOutletId] = useState(user?.outlet_id || '');
  const [loadingOutlets, setLoadingOutlets] = useState(true);

  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pinDialog, setPinDialog] = useState(null); // {name, pin}
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: '', role: 'cashier' });

  // Load outlets the caller can see (tenant-scoped; admin sees all).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/outlets`, { headers: getHeaders() });
        if (!alive) return;
        setOutlets(r.data || []);
        if (!outletId && (r.data || []).length > 0) {
          setOutletId(r.data[0].id);
        }
      } catch (_e) {
        toast.error('Could not load outlets');
      } finally {
        if (alive) setLoadingOutlets(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    if (!outletId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/pos/staff`, {
        headers: getHeaders(),
        params: { outlet_id: outletId },
      });
      setStaff(r.data.staff || []);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not load staff');
      setStaff([]);
    } finally { setLoading(false); }
  }, [API, getHeaders, outletId]);

  useEffect(() => { if (outletId) load(); }, [outletId, load]);

  const selectedOutlet = useMemo(
    () => outlets.find((o) => o.id === outletId),
    [outlets, outletId],
  );

  const addStaff = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (!outletId) { toast.error('Pick an outlet first'); return; }
    try {
      const r = await axios.post(`${API}/pos/staff`,
        { name: form.name.trim(), role: form.role, outlet_id: outletId },
        { headers: getHeaders() });
      setPinDialog({ name: r.data.staff.name, pin: r.data.pin });
      setForm({ name: '', role: 'cashier' });
      setAddOpen(false);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not add staff');
    }
  };

  const resetPin = async (s) => {
    if (!window.confirm(`Reset ${s.name}'s PIN? The old PIN stops working immediately.`)) return;
    try {
      const r = await axios.post(`${API}/pos/staff/${s.id}/reset-pin`, {},
        { headers: getHeaders() });
      setPinDialog({ name: r.data.staff.name, pin: r.data.pin });
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not reset PIN');
    }
  };

  const toggleActive = async (s) => {
    try {
      await axios.patch(`${API}/pos/staff/${s.id}`, { active: !s.active },
        { headers: getHeaders() });
      toast.success(`${s.name} ${s.active ? 'deactivated' : 'reactivated'}`);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not update');
    }
  };

  const changeRole = async (s, role) => {
    try {
      await axios.patch(`${API}/pos/staff/${s.id}`, { role },
        { headers: getHeaders() });
      toast.success(`${s.name} is now ${ROLE_OPTIONS.find((r) => r.value === role)?.label || role}`);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not change role');
    }
  };

  if (loadingOutlets) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin w-8 h-8 border-2 border-[color:var(--color-primary)] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!loadingOutlets && outlets.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <ShieldAlert className="w-8 h-8 mb-3 text-amber-600" />
        <div className="text-lg font-semibold text-[#1A2421] mb-1">No outlets to manage</div>
        <div className="text-sm text-[#5C6B64] mb-6">Add an outlet first, then return here to configure POS access.</div>
        <Button variant="outline"
                className="border-[#E2E8DF] text-[#1A2421]"
                onClick={() => navigate('/admin/outlets')}
                data-testid="go-to-outlets">
          Go to outlets
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="pos-settings">
      {/* Page header — mirrors AdminOutlets pattern */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <button className="inline-flex items-center gap-1.5 text-sm text-[#5C6B64] hover:text-[color:var(--color-primary)] mb-2"
                  onClick={() => navigate('/pos')}
                  data-testid="back-to-terminal">
            <ArrowLeft className="w-4 h-4" /> Back to terminal
          </button>
          <h2 className="text-lg sm:text-xl tracking-tight font-semibold text-[#1A2421] flex items-center gap-2">
            <Users className="w-5 h-5 text-[color:var(--color-primary)]" /> Staff &amp; PIN Access
          </h2>
          <p className="text-sm text-[#5C6B64] mt-1 max-w-2xl">
            Assign staff to this outlet, choose their POS role and generate a PIN. PINs are
            generated by the system and shown once — never retrievable again. Deactivate
            rather than delete so historical records keep the name.
          </p>
        </div>
        <Button onClick={() => setAddOpen(true)}
                disabled={!outletId}
                className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white"
                data-testid="add-staff-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Add staff
        </Button>
      </div>

      {/* Outlet picker */}
      <Card className="bg-white border-[#E2E8DF] mb-4">
        <CardContent className="p-4">
          <Label className="text-[#5C6B64] flex items-center gap-1.5 mb-2 text-xs uppercase tracking-wide font-medium">
            <Store className="w-3.5 h-3.5" /> Outlet
          </Label>
          <Select value={outletId} onValueChange={setOutletId}>
            <SelectTrigger className="bg-white border-[#E2E8DF] text-[#1A2421] w-full sm:w-[360px]"
                           data-testid="outlet-picker">
              <SelectValue placeholder="Select an outlet" />
            </SelectTrigger>
            <SelectContent>
              {outlets.map((o) => (
                <SelectItem key={o.id} value={o.id} data-testid={`outlet-option-${o.id}`}>
                  {o.name}{o.city ? ` · ${o.city}` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedOutlet ? (
            <div className="text-xs text-[#5C6B64] mt-2" data-testid="outlet-picker-hint">
              Managing POS staff for <strong className="text-[#1A2421]">{selectedOutlet.name}</strong>. Switch outlets above to manage staff elsewhere.
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* Staff table */}
      <Card className="bg-white border-[#E2E8DF]">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F9F9F8] text-[#5C6B64] text-left text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Role</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Last login</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody data-testid="staff-list" className="text-[#1A2421]">
                {loading ? (
                  <tr><td colSpan={5} className="px-4 py-10 text-center text-[#5C6B64]">Loading…</td></tr>
                ) : staff.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-10 text-center text-[#5C6B64]" data-testid="staff-empty">
                    No staff yet at this outlet. Tap “Add staff” to create the first PIN.
                  </td></tr>
                ) : staff.map((s) => (
                  <tr key={s.id} className="border-t border-[#EEF1EE] hover:bg-[#F9F9F8]" data-testid={`staff-row-${s.id}`}>
                    <td className="px-4 py-3 font-medium">{s.name}</td>
                    <td className="px-4 py-3">
                      <Select value={s.role} onValueChange={(v) => changeRole(s, v)}>
                        <SelectTrigger className="h-8 w-[150px] bg-white border-[#E2E8DF] text-[#1A2421] text-xs"
                                       data-testid={`role-select-${s.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLE_OPTIONS.map((r) => (
                            <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3">
                      {s.active
                        ? <Badge className="bg-[color:var(--color-primary-tint)] text-[color:var(--color-primary)] border-[color:var(--color-primary-tint)] hover:bg-[color:var(--color-primary-tint)]">Active</Badge>
                        : <Badge className="bg-[#F1F5F3] text-[#5C6B64] border-[#E2E8DF] hover:bg-[#F1F5F3]">Inactive</Badge>}
                      {s.locked ? <Badge className="ml-1 bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-50">Locked</Badge> : null}
                    </td>
                    <td className="px-4 py-3 text-xs text-[#5C6B64]">{s.last_login_at || '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="ghost"
                              onClick={() => resetPin(s)}
                              data-testid={`reset-pin-${s.id}`}
                              className="text-[#5C6B64] hover:text-[color:var(--color-primary)] hover:bg-[#F9F9F8]">
                        <KeyRound className="w-4 h-4 mr-1" /> Reset PIN
                      </Button>
                      <Button size="sm" variant="ghost"
                              onClick={() => toggleActive(s)}
                              data-testid={`toggle-active-${s.id}`}
                              className="text-[#5C6B64] hover:text-[color:var(--color-primary)] hover:bg-[#F9F9F8]">
                        <Power className="w-4 h-4 mr-1" /> {s.active ? 'Deactivate' : 'Activate'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* POS Appearance — feature 6, terminal theme */}
      {outletId ? (
        <PosAppearanceSection outletId={outletId} outletName={selectedOutlet?.name} />
      ) : null}

      {/* Invoice Numbering — Sprint 3B correction · deadlock fix */}
      {outletId ? (
        <PosInvoiceConfigSection outletId={outletId} outletName={selectedOutlet?.name} />
      ) : null}

      {/* Terminal Security — Sprint 3B correction · idle-lock configurable */}
      {outletId ? (
        <PosSecuritySettingsSection outletId={outletId} outletName={selectedOutlet?.name} />
      ) : null}

      {/* Add staff dialog — shadcn default (white surface) */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-staff-dialog">
          <DialogHeader>
            <DialogTitle>Add staff</DialogTitle>
            <DialogDescription>
              A PIN will be generated by the system and shown once. It cannot be recovered later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     className="mt-1"
                     data-testid="add-staff-name" />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1" data-testid="add-staff-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={addStaff}
                    className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white"
                    data-testid="add-staff-confirm">Generate PIN &amp; add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* One-time PIN reveal — the ONE emphasised surface. Uses amber only
          for the warning line, not for the card chrome. */}
      <Dialog open={!!pinDialog} onOpenChange={(o) => !o && setPinDialog(null)}>
        <DialogContent data-testid="pin-reveal-dialog">
          <DialogHeader>
            <DialogTitle>PIN for {pinDialog?.name}</DialogTitle>
            <DialogDescription className="text-amber-800">
              This PIN is shown exactly once. It is not stored in plain text and cannot be
              retrieved again. Share it with the staff member now.
            </DialogDescription>
          </DialogHeader>
          <div className="py-6 text-center">
            <div className="text-5xl font-mono tracking-[0.4em] text-[color:var(--color-primary)]"
                 data-testid="pin-reveal-value">
              {pinDialog?.pin}
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setPinDialog(null)}
                    className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white"
                    data-testid="pin-reveal-close">
              I have given the PIN to the staff member
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
